import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    Container,
    Text,
    Content,
    Row,
    Col,
    RadioGroup,
    Radio,
    FlexboxGrid,
    Grid,
} from 'rsuite'
import FlowPageLayout from '../../components/layout/FlowPageLayout'
import PortalHeader from '../../components/layout/PortalHeader'
import {
    getFlowFormValidationRuleWithRequirement,
    requiredValidation,
    validationCompanyNameRegex,
    validationNameRegex,
} from '../../common/validation'
import PersonDetails from '../../components/form/PersonDetails'
import { FLOWS_ENUM } from '../../common/enums'
import { selectPermitRegistrant, selectUpdatedPermitData } from '../../store/slices/update/updateSelectors'
import { updatePermitData } from '../../store/slices/update/updateSlice'
import { LOCAL_CONTACT_OPTIONS, PROPERTY_MANAGEMENT_OPTIONS, REGISTRANT_OPTIONS } from '../../components/payment/constants'
import {
    selectConfigExpressLocalContactVerbiage,
    selectConfigExpressRegistrationLocalContactOptionalFields,
    selectConfigExpressRegistrationPeopleLocalContactDescription,
    selectConfigExpressRegistrationPeoplePropertyManagementDescription,
    selectConfigExpressRegistrationPropertyManagerEnabled,
    selectConfigExpressRegistrationPropertyManagerOptionalFields
} from '../../store/slices/config/configSelectors'
import { beginsWithVowel } from '../../common/utils'
import SchemaTyped from 'rsuite/esm/Schema/Schema'

const UpdateContacts = () => {
    const dispatch = useDispatch()
    const propertyManagerForm = useRef()
    const ownerRepForm = useRef()
    const [propertyManagerFormValues, setPropertyManagerFormValues] = useState({})
    const [ownerRepFormValues, setOwnerRepFormValues] = useState({})
    const [propertyManagerFormError, setPropertyManagerFormError] = useState({})
    const [ownerRepFormError, setOwnerRepFormError] = useState({})
    const [isManagedByPropertyManager, setIsManagedByPropertyManager] = useState(false)
    const [isPropertyOwnerAlsoOwnerRep, setIsPropertyOwnerAlsoOwnerRep] = useState(true)
    const [isPropertyManagerAlsoOwnerRep, setIsPropertyManagerAlsoOwnerRep] = useState(false)
    const [isOwnerRepOther, setIsOwnerRepOther] = useState(false)
    const [isSelfManaged, setIsSelfManaged] = useState(true);
    const [isOwnerRepMyself, setIsOwnerRepMyself] = useState(true);
    const defaultOwnerRepRadioOption = LOCAL_CONTACT_OPTIONS.MYSELF;
    const [selectedOwnerRepRadioOption, setSelectedOwnerRepRadioOption] = useState(LOCAL_CONTACT_OPTIONS.MYSELF)
    const defaultManagementRadioOption = PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED;
    const [selectedManagementRadioOption, setSelectedManagementRadioOption] = useState(PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED)

    const selectedRegistrant = useSelector(selectPermitRegistrant);

    const { contacts, registrant, property } = useSelector(selectUpdatedPermitData);
    const selectedLocalContactVerbiage = useSelector(selectConfigExpressLocalContactVerbiage);
    const selectedPropertyManagerEnabled = useSelector(selectConfigExpressRegistrationPropertyManagerEnabled)
    const selectedPropertyManagerOptionalFields = useSelector(selectConfigExpressRegistrationPropertyManagerOptionalFields)
    const selectedLocalContactOptionalFields = useSelector(selectConfigExpressRegistrationLocalContactOptionalFields);
    const propertyManagerDescription = useSelector(selectConfigExpressRegistrationPeoplePropertyManagementDescription);
    const localContactDescription = useSelector(selectConfigExpressRegistrationPeopleLocalContactDescription);

    const [hasData, setHasData] = useState(contacts !== undefined)

    useEffect(() => {
        setHasData(contacts !== undefined)
    }, [contacts])

    useEffect(() => {
        if (hasData) {
            setSelectedManagementRadioOption(prev => contacts?.selectedManagementOption ?? prev);
            setSelectedOwnerRepRadioOption(prev => contacts?.selectedOwnerRepOption ?? prev);
            if (contacts.selectedOwnerRepOption === LOCAL_CONTACT_OPTIONS.OTHER) {
                setOwnerRepFormValues({
                    ...contacts.localContact,
                    sameAsPropertyAddress: contacts.localContact?.mailingAddress && contacts.localContact?.mailingAddress.toLowerCase() === property?.propertyaddress.toLowerCase()
                })
            }
            if (contacts.selectedManagementOption === PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER) {
                setPropertyManagerFormValues({
                    ...contacts.propertyManager,
                    sameAsPropertyAddress: contacts.propertyManager?.mailingAddress && contacts.propertyManager?.mailingAddress.toLowerCase() === property?.propertyaddress.toLowerCase()
                });
            }
        }
    }, [hasData, contacts, registrant])

    useEffect(() => {
        setIsOwnerRepMyself(selectedOwnerRepRadioOption === LOCAL_CONTACT_OPTIONS.MYSELF);
        setIsPropertyOwnerAlsoOwnerRep(
            selectedOwnerRepRadioOption === LOCAL_CONTACT_OPTIONS.OWNER ||
            selectedRegistrant?.registrantType === REGISTRANT_OPTIONS.OWNER &&
            selectedOwnerRepRadioOption === LOCAL_CONTACT_OPTIONS.MYSELF
        );
        setIsOwnerRepOther(selectedOwnerRepRadioOption === LOCAL_CONTACT_OPTIONS.OTHER);
        setIsPropertyManagerAlsoOwnerRep(
            selectedOwnerRepRadioOption === LOCAL_CONTACT_OPTIONS.PROPERTY_MANAGER ||
            selectedRegistrant?.registrantType === REGISTRANT_OPTIONS.PROPERTY_MANAGER &&
            selectedOwnerRepRadioOption === LOCAL_CONTACT_OPTIONS.MYSELF
        );
    }, [selectedOwnerRepRadioOption])

    useEffect(() => {
        setIsSelfManaged(selectedManagementRadioOption === PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED);
        setIsManagedByPropertyManager(selectedManagementRadioOption === PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER);
    }, [selectedManagementRadioOption])

    const propertyManagerFormModel = useMemo(() => {
        return {
            firstName: contacts?.propertyManager?.firstName ?? '',
            lastName: contacts?.propertyManager?.lastName ?? '',
            companyName: contacts?.propertyManager?.companyName ?? '',
            email: contacts?.propertyManager?.email ?? '',
            phone: contacts?.propertyManager?.phone ?? '',
            mailingAddress: contacts?.propertyManager?.mailingAddress ?? '',
        }
    }, [])

    const showPropertyManagerQuestion = useMemo(() => {
        return selectedPropertyManagerEnabled && selectedRegistrant?.registrantType !== REGISTRANT_OPTIONS.PROPERTY_MANAGER;
    }, [selectedRegistrant, selectedPropertyManagerEnabled]);

    const showTheOwnerLocalContactOption = useMemo(() => {
        return selectedRegistrant?.registrantType !== REGISTRANT_OPTIONS.OWNER;
    }, [selectedRegistrant]);

    const localContactMyselfOptionText = useMemo(() => {
        const registrantType = selectedRegistrant?.registrantType;
        switch (registrantType) {
            case REGISTRANT_OPTIONS.OWNER:
                return "Myself (the Owner)";
            case REGISTRANT_OPTIONS.PROPERTY_MANAGER:
                return "Myself (the Property Manager)";
            case REGISTRANT_OPTIONS.TENANT:
                return "Myself (the Tenant)";
            default:
                return "Myself";
        }
    }, [selectedRegistrant]);

    const ownerRepFormModel = useMemo(() => {
        return {
            firstName: contacts?.localContact?.firstName ?? '',
            lastName: contacts?.localContact?.lastName ?? '',
            companyName: contacts?.localContact?.companyName ?? '',
            email: contacts?.localContact?.email ?? '',
            phone: contacts?.localContact?.phone ?? '',
            mailingAddress: contacts?.localContact?.mailingAddress ?? '',
        }
    }, [])

    const {
        StringType
    } = SchemaTyped.Types;

    const propertyManagerValidationModel = {
        firstName: getFlowFormValidationRuleWithRequirement("firstName", selectedPropertyManagerOptionalFields, StringType().pattern(validationNameRegex, "Must be all letters")),
        lastName: getFlowFormValidationRuleWithRequirement("lastName", selectedPropertyManagerOptionalFields, StringType().pattern(validationNameRegex, "Must be all letters")),
        companyName: getFlowFormValidationRuleWithRequirement("companyName", {"companyName": 'optional'}, StringType().pattern(validationCompanyNameRegex, "Company names must begin with ., @, & or an alphanumeric character.  The remainder of the name may only include -, !, ., @, &, (, ), alphanumeric characters or a single space character.")),
        email: getFlowFormValidationRuleWithRequirement("email", selectedPropertyManagerOptionalFields, StringType().isEmail()),
        phone: getFlowFormValidationRuleWithRequirement("phone", selectedPropertyManagerOptionalFields, StringType().pattern(/^\d{10}$/, "A 10 digit phone number is required")),
        mailingAddress: requiredValidation,
    }

    const ownerRepValidationModel = {
        firstName: getFlowFormValidationRuleWithRequirement("firstName", selectedLocalContactOptionalFields, StringType().pattern(validationNameRegex, "Must be all letters")),
        lastName: getFlowFormValidationRuleWithRequirement("lastName", selectedLocalContactOptionalFields, StringType().pattern(validationNameRegex, "Must be all letters")),
        companyName: getFlowFormValidationRuleWithRequirement("companyName", {"companyName": 'optional'}, StringType().pattern(validationCompanyNameRegex, "Company names must begin with ., @, & or an alphanumeric character.  The remainder of the name may only include -, !, ., @, &, (, ), alphanumeric characters or a single space character.")),
        email: getFlowFormValidationRuleWithRequirement("email", selectedLocalContactOptionalFields, StringType().isEmail()),
        phone: getFlowFormValidationRuleWithRequirement("phone", selectedLocalContactOptionalFields, StringType().pattern(/^\d{10}$/, "A 10 digit phone number is required")),
        mailingAddress: requiredValidation,
    }

    const showOrHidePropertyManagerEntry = (v) => {
        setSelectedManagementRadioOption(v);
    }

    const showOrHideOwnerRepOther = (v) => {
        setSelectedOwnerRepRadioOption(v);
    }

    const updateApplicationData = () => {
        const data = {
            contacts: {},
        }

        data.contacts.selectedManagementOption = selectedManagementRadioOption;
        data.contacts.selectedOwnerRepOption = selectedOwnerRepRadioOption;

        if (selectedRegistrant.registrantType === REGISTRANT_OPTIONS.OWNER) {
            // Registrant is OWNER
            // Gather property management details
            if (!isSelfManaged) {
                // Property Management Company is PROPERTY MANAGER
                // Details from form on this page
                if (!propertyManagerForm?.current?.check()) return false
                data.contacts.propertyManager = { ...propertyManagerFormValues }
            } else {
                data.contacts.propertyManager = { ...selectedRegistrant?.owner[0] };
            }

            // Gather owner rep details
            if (isPropertyOwnerAlsoOwnerRep) {
                // Owner also represents themselves, details from previous page
                data.contacts.localContact = { ...selectedRegistrant?.owner[0] }
            } else if (isPropertyManagerAlsoOwnerRep && data.contacts?.propertyManager) {
                // The property manager is the owner rep, details from form on this page
                data.contacts.localContact = { ...data.contacts.propertyManager }
            } else {
                // Some one else represents owner
                if (!ownerRepForm.current?.check()) return false;
                data.contacts.localContact = { ...ownerRepFormValues }
            }
        } else if (selectedRegistrant.registrantType === REGISTRANT_OPTIONS.PROPERTY_MANAGER) {
            // Registrant is PROPERTY MANAGER
            // Gather property management details
            data.contacts.propertyManager = { ...selectedRegistrant?.registrant }

            // Gather owner rep details
            if (isOwnerRepMyself) {
                // Details from previous page
                data.contacts.localContact = { ...selectedRegistrant?.registrant }
            } else if (isPropertyOwnerAlsoOwnerRep) {
                // Details from previous page
                data.contacts.localContact = { ...selectedRegistrant?.owner[0] }
            } else {
                // Details from form on this page
                if (!ownerRepForm.current?.check()) return false;
                data.contacts.localContact = { ...ownerRepFormValues }
            }
        } else { // Registrant is Tenant
            // Gather property management details
            if (isSelfManaged) {
                data.contacts.propertyManager = { ...selectedRegistrant?.registrant };
            } else {
                // Details from form on this page
                if (!propertyManagerForm?.current?.check()) return false
                data.contacts.propertyManager = { ...propertyManagerFormValues }
            }

            // Gather owner rep details
            if (isOwnerRepMyself) {
                // Details from previous page
                data.contacts.localContact = { ...selectedRegistrant?.registrant }
            } else if (isPropertyOwnerAlsoOwnerRep) {
                // Details from previous page
                data.contacts.localContact = { ...selectedRegistrant?.owner[0] }
            } else if (isPropertyManagerAlsoOwnerRep && data.contacts?.propertyManager) {
                // The property manager is the owner rep, details from form on this page
                data.contacts.localContact = { ...data.contacts.propertyManager }
            } else {
                // Details from form on this page
                if (!ownerRepForm.current?.check()) return false;
                data.contacts.localContact = { ...ownerRepFormValues }
            }
        }
        // Clean up
        delete data.contacts.localContact.sameAsPropertyAddress;
        delete data.contacts.propertyManager.sameAsPropertyAddress;

        dispatch(updatePermitData({ ...data }));
        return true;
    }

    const dispatchContactDetails = () => {
        if (!selectedRegistrant?.registrantType) return false;
        return updateApplicationData();
    }

    const handleBackClick = () => {
        // don't validate on back click
        // it will be validated next time they go next on this page
        updateApplicationData();
        return true;
    }

    const onOwnerRepSameAsPropertyAddressChanged = (checked) => {
        setOwnerRepFormValues(prevFormValues => (
            {
                ...prevFormValues,
                sameAsPropertyAddress: checked,
                mailingAddress: checked ? property?.propertyaddress : prevFormValues.mailingAddress
            }
        ))
    }

    const onPropertyManagerSameAsPropertyAddressChanged = (checked) => {
        setPropertyManagerFormValues(prevFormValues => (
            {
                ...prevFormValues,
                sameAsPropertyAddress: checked,
                mailingAddress: checked ? property?.propertyaddress : prevFormValues.mailingAddress
            }
        ))
    }

    return (
        <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={24}>
                <PortalHeader flow={FLOWS_ENUM.UPDATE_CERTIFICATE} />
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={24}>
                <Content>
                    <FlowPageLayout
                        title="Property Contacts"
                        onNext={dispatchContactDetails}
                        onBack={handleBackClick}
                    >
                        <Grid className="mt-12 ml-0 " xs={24} lg={12} fluid>
                            {
                                showPropertyManagerQuestion &&
                                (
                                    <>
                                        <Row className="mb-16">
                                            <Col xs={24}>
                                                <Text className="text-bold mb-8">
                                                    Property Management Company
                                                </Text>
                                                <Text>
                                                    {propertyManagerDescription}
                                                </Text>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col xs={24}>
                                                <Text>
                                                    Are you using a Property Manager for this property?
                                                </Text>
                                                <RadioGroup
                                                    name="owner-radio-option-group"
                                                    defaultValue={defaultManagementRadioOption}
                                                    value={selectedManagementRadioOption}
                                                    onChange={showOrHidePropertyManagerEntry}
                                                >
                                                    <Radio value={PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED}>
                                                        Self Managed
                                                    </Radio>
                                                    {
                                                        selectedPropertyManagerEnabled !== false && (
                                                            <Radio value={PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER}>
                                                                Property Manager
                                                            </Radio>
                                                        )
                                                    }

                                                </RadioGroup>
                                            </Col>
                                        </Row>
                                        {
                                            isManagedByPropertyManager ? (
                                                <>
                                                    <Row className="mt-8 mb-8">
                                                        <Col xs={12}>
                                                            <Text
                                                                style={{ fontWeight: 'bold' }}
                                                            >
                                                                Property Manager Information
                                                            </Text>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <PersonDetails
                                                                ref={propertyManagerForm}
                                                                formModel={propertyManagerFormModel}
                                                                formError={propertyManagerFormError}
                                                                formValues={propertyManagerFormValues}
                                                                validationModel={propertyManagerValidationModel}
                                                                setFormError={setPropertyManagerFormError}
                                                                setFormValues={setPropertyManagerFormValues}
                                                                showSameAsPropertyAddressCheckboxOption={true}
                                                                onSameAsPropertyAddressChanged={onPropertyManagerSameAsPropertyAddressChanged}
                                                                optionalFields={selectedPropertyManagerOptionalFields}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </>
                                            ) : null
                                        }
                                    </>
                                )
                            }

                            <Row className={`mb-16 ${selectedRegistrant?.registrantType !== REGISTRANT_OPTIONS.PROPERTY_MANAGER && "mt-24"}`}>
                                <Col xs={24}>
                                    <Text className="text-bold mb-8">
                                        {selectedLocalContactVerbiage}
                                    </Text>
                                    <Text>
                                        {localContactDescription}
                                    </Text>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={24}>
                                    <Text>
                                        Who is the {selectedLocalContactVerbiage.toLowerCase()} for this
                                        property?
                                    </Text>
                                    <RadioGroup
                                        name="owner-rep-radio-option-group"
                                        defaultValue={defaultOwnerRepRadioOption}
                                        value={selectedOwnerRepRadioOption}
                                        onChange={showOrHideOwnerRepOther}
                                    >
                                        <Radio value={LOCAL_CONTACT_OPTIONS.MYSELF}>
                                            {localContactMyselfOptionText}
                                        </Radio>
                                        {
                                            isManagedByPropertyManager && (
                                                <Radio value={LOCAL_CONTACT_OPTIONS.PROPERTY_MANAGER}>
                                                    The Property Manager
                                                </Radio>
                                            )
                                        }
                                        {
                                            showTheOwnerLocalContactOption && (
                                                <Radio value={LOCAL_CONTACT_OPTIONS.OWNER}>
                                                    The Owner
                                                </Radio>
                                            )
                                        }
                                        <Radio value={LOCAL_CONTACT_OPTIONS.OTHER}>
                                            Someone else
                                        </Radio>
                                    </RadioGroup>
                                </Col>
                            </Row>
                            {isOwnerRepOther ? (
                                <>
                                    <Row className="mt-24 mb-8">
                                        <Col xs={12}>
                                            <Text
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                {selectedLocalContactVerbiage} Information
                                            </Text>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <PersonDetails
                                                ref={ownerRepForm}
                                                formModel={ownerRepFormModel}
                                                formError={ownerRepFormError}
                                                formValues={ownerRepFormValues}
                                                validationModel={ownerRepValidationModel}
                                                setFormError={setOwnerRepFormError}
                                                setFormValues={setOwnerRepFormValues}
                                                showSameAsPropertyAddressCheckboxOption={true}
                                                onSameAsPropertyAddressChanged={onOwnerRepSameAsPropertyAddressChanged}
                                                optionalFields={selectedLocalContactOptionalFields}
                                            />
                                        </Col>
                                    </Row>
                                </>
                            ) : null}
                        </Grid>
                    </FlowPageLayout>
                </Content>
            </FlexboxGrid.Item>
        </FlexboxGrid>
    )
}

export default UpdateContacts
